import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';
import { useLeadFormStore } from '../../../store/leadFormStore';
import { projectService } from '../../../services/project.service';
import { scheduleService, getAvailableSlotsForDate } from '../../../services/schedule.service';
import { appointmentService } from '../../../services/appointment.service';
import { Schedule, TimeSlot } from '../../../types/schedule.types';
import { format, addDays } from 'date-fns';
import { useProject } from '../../../contexts/ProjectContext';
import { formatTimeRange } from '../../../utils/dateUtils';

interface StepProps {
  onNext: () => void;
  onBack?: () => void;
}

const ScheduleStep: React.FC<StepProps> = ({ onNext, onBack }) => {
  const { formData, setScheduleData, projectId } = useLeadFormStore();
  const { selectedProject } = useProject();
  const projectTimezone = selectedProject?.settings?.timezone || '';
  const [clientSchedules, setClientSchedules] = useState<Schedule[]>([]);
  const [slotCounts, setSlotCounts] = useState<Map<string, number>>(new Map());
  const [availableSlots, setAvailableSlots] = useState<{ [date: string]: TimeSlot[] }>({});
  const [loading, setLoading] = useState(true);
  
  // Initialize schedule data if not present
  const scheduleData = formData.schedule || {
    interestedInTour: false,
    interestedInInfo: false,
    preferredDates: [],
    preferredTimes: []
  };

  // Generate next 14 days for date selection
  const availableDates = Array.from({ length: 14 }, (_, i) => addDays(new Date(), i));

  useEffect(() => {
    const loadProjectSchedules = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      try {
        // Get project details
        const project = await projectService.getProject(projectId);

        if (!project) {
          setLoading(false);
          return;
        }

        // Get client users from project
        const clientUsers = project.assignedUsers.filter(
          (u: any) => u.role === 'client_user' || u.role === 'client_admin'
        );

        // Load all schedules for the project and filter by client users
        const allProjectSchedules = await scheduleService.getSchedulesByProject(projectId);
        const clientUserIds = clientUsers.map((u: any) => u.userId);
        const clientScheduleList = allProjectSchedules.filter(s => clientUserIds.includes(s.userId));

        // Scope to the chosen campus: campus-tagged schedules for this lead's
        // campus plus untagged (project-wide) ones. No campus chosen → all.
        const segId = formData.schoolSegmentId;
        const validSchedules = segId
          ? clientScheduleList.filter(s => !s.schoolSegmentId || s.schoolSegmentId === segId)
          : clientScheduleList;

        setClientSchedules(validSchedules);

        // Process schedules to find available slots for each date
        processAvailableSlots(validSchedules);
      } catch (error) {
        console.error('Error loading schedules:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProjectSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, formData.schoolSegmentId]);


  // Load already booked appointments
  useEffect(() => {
    const loadBookedAppointments = async () => {
      if (!projectId) return;

      try {
        const appointments = await appointmentService.getAppointmentsByProject(projectId);
        const counts = new Map<string, number>();

        appointments.forEach(apt => {
          // Tally per slot (date-startTime-endTime-userId) so we can compare
          // against the schedule's defaultSlotCapacity rather than hiding the
          // slot after the first booking.
          const key = `${apt.date}-${apt.startTime}-${apt.endTime}-${apt.userId}`;
          counts.set(key, (counts.get(key) || 0) + 1);
        });

        setSlotCounts(counts);
      } catch (error) {
        console.error('Error loading booked appointments:', error);
      }
    };

    loadBookedAppointments();
  }, [projectId, scheduleData.preferredDates]); // Reload when dates change

  const processAvailableSlots = (schedules: Schedule[]) => {
    const slotsByDate: { [date: string]: TimeSlot[] } = {};

    availableDates.forEach(date => {
      const dayOfWeek = date.getDay();
      const dateStr = format(date, 'yyyy-MM-dd');

      // Combine all available slots from all client schedules for this day
      const daySlots: TimeSlot[] = [];

      schedules.forEach(schedule => {
        // Exceptions still take precedence over the centralized resolver.
        const exception = schedule.exceptions?.find(e => e.date === dateStr);
        if (exception) {
          if (exception.type === 'unavailable') return;
          if (exception.timeSlots) {
            daySlots.push(...exception.timeSlots);
            return;
          }
        }

        // Centralized 30-min slot generator. Handles dateAvailability,
        // hourlyAvailability, and legacy timeSlots, with whole-hour expansion.
        const slots = getAvailableSlotsForDate(schedule, date);
        const capacity = schedule.defaultSlotCapacity ?? 1;
        slots.forEach(({ startTime, endTime }) => {
          daySlots.push({
            dayOfWeek,
            startTime,
            endTime,
            capacity,
            isRecurring: false,
            id: `${schedule.userId}-${dateStr}-${startTime}`,
            userName: schedule.userName,
            userId: schedule.userId,
          } as TimeSlot & { userName: string; userId: string });
        });
      });

      // Sort slots by start time
      daySlots.sort((a, b) => a.startTime.localeCompare(b.startTime));

      if (daySlots.length > 0) {
        slotsByDate[dateStr] = daySlots;
      }
    });

    setAvailableSlots(slotsByDate);
  };

  const updateScheduleData = (updates: any) => {
    setScheduleData({
      ...scheduleData,
      ...updates
    });
  };

  const handleDateSelect = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const currentDates = scheduleData.preferredDates || [];
    
    if (currentDates.includes(dateStr)) {
      // Remove date
      updateScheduleData({
        preferredDates: currentDates.filter((d: string) => d !== dateStr)
      });
    } else {
      // Add date
      updateScheduleData({
        preferredDates: [...currentDates, dateStr]
      });
    }
  };

  const handleTimeSelect = (time: string) => {
    const currentTimes = scheduleData.preferredTimes || [];
    
    if (currentTimes.includes(time)) {
      updateScheduleData({
        preferredTimes: currentTimes.filter((t: string) => t !== time)
      });
    } else {
      const updates: any = {
        preferredTimes: [...currentTimes, time]
      };
      
      // Extract userId if it exists (format: date-slotId|userId)
      if (time.includes('|')) {
        const userId = time.split('|')[1];
        if (userId) {
          updates.selectedClientId = userId;
        }
      }
      
      updateScheduleData(updates);
    }
  };

  const handleSubmit = () => {
    onNext();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading available schedules...</div>
      </div>
    );
  }

  if (clientSchedules.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No client schedules available for this project.</p>
          <p className="text-sm text-gray-500 mt-2">
            Please contact your project administrator to set up client availability.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Schedule Tour/Meeting</h3>
        
        <div className="grid grid-cols-1 gap-6">
          {/* Interest Options */}
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={scheduleData.interestedInTour || false}
                onChange={(e) => updateScheduleData({ interestedInTour: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Interested in scheduling a tour</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={scheduleData.interestedInInfo || false}
                onChange={(e) => updateScheduleData({ interestedInInfo: e.target.checked })}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Would like more information</span>
            </label>
          </div>

          {/* Date Selection */}
          {(scheduleData.interestedInTour || scheduleData.interestedInInfo) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Preferred Dates
              </label>
              <div className="grid grid-cols-7 gap-2">
                {availableDates.map((date) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const hasSlots = !!availableSlots[dateStr];
                  const isSelected = (scheduleData.preferredDates || []).includes(dateStr);
                  
                  return (
                    <button
                      key={dateStr}
                      type="button"
                      onClick={() => hasSlots && handleDateSelect(date)}
                      disabled={!hasSlots}
                      className={`
                        p-2 text-center rounded-md text-sm
                        ${isSelected 
                          ? 'bg-indigo-600 text-white' 
                          : hasSlots
                            ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        }
                      `}
                    >
                      <div className="font-medium">{format(date, 'EEE')}</div>
                      <div>{format(date, 'd')}</div>
                      {hasSlots && (
                        <div className="text-xs mt-1">
                          {availableSlots[dateStr].length} slots
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Time Slot Selection */}
          {(scheduleData.preferredDates || []).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Available Time Slots
              </label>
              {(scheduleData.preferredDates || []).map((dateStr: string) => {
                const slots = availableSlots[dateStr] || [];
                return (
                  <div key={dateStr} className="mb-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">
                      {format(new Date(dateStr), 'EEEE, MMMM d')}
                    </h4>
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((slot: any) => {
                        const slotKey = `${dateStr}-${slot.startTime}-${slot.endTime}-${slot.userId}`;
                        const slotCapacity = slot.capacity ?? 1;
                        const bookedCount = slotCounts.get(slotKey) || 0;
                        const isBooked = bookedCount >= slotCapacity;
                        const timeLabel = formatTimeRange(slot.startTime, slot.endTime, projectTimezone);
                        
                        const slotId = `${dateStr}-${slot.id}-${slot.startTime}-${slot.endTime}|${slot.userId}`;
                        const isSelected = (scheduleData.preferredTimes || []).includes(slotId);
                        
                        return (
                          <button
                            key={slot.id}
                            type="button"
                            onClick={() => !isBooked && handleTimeSelect(slotId)}
                            disabled={isBooked}
                            className={`
                              p-3 rounded-md text-sm flex flex-col items-center
                              ${isBooked
                                ? 'bg-red-100 text-red-600 cursor-not-allowed opacity-60'
                                : isSelected
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                              }
                            `}
                          >
                            <Clock className="w-4 h-4 mb-1" />
                            <span>{timeLabel}</span>
                            {(slotCapacity > 1 || bookedCount > 0) && (
                              <span className={`text-xs mt-1 px-2 py-0.5 rounded-full ${
                                isBooked
                                  ? 'bg-red-200 text-red-800 font-bold'
                                  : bookedCount > 0
                                    ? 'bg-amber-100 text-amber-700'
                                    : isSelected
                                      ? 'bg-white/20 text-white'
                                      : 'bg-gray-200 text-gray-600'
                              }`}>
                                {bookedCount}/{slotCapacity}{isBooked ? ' full' : ' booked'}
                              </span>
                            )}

                            {slot.userName && (
                              <span className="text-xs opacity-75 mt-1">
                                with {slot.userName}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Back
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="flex items-center px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Next
          <ChevronRight className="w-5 h-5 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default ScheduleStep;
